import { tokens } from '@/locales/translations/tokens';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VideoStreamProps {
  videoId: string;
  currentTime?: number;
  onVideoLoading?: (loading: boolean) => void;
  onVideoError?: (hasError: boolean) => void;
  className?: string;
  width?: string;
  height?: string; 
  controls?: boolean;
  responsive?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  customerCode?: string;
}

export const VideoStream: React.FC<VideoStreamProps> = ({
  videoId,
  currentTime = 0,
  onVideoLoading,
  onVideoError,
  className = "w-full relative z-30 aspect-video h-full",
  width = '100%',
  height = '100%',
  controls = true,
  responsive = true,
  preload = 'metadata',
  customerCode
}) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const buildIframeUrl = () => {
    const baseUrl = customerCode 
      ? `https://customer-${customerCode}.cloudflarestream.com/${videoId}/iframe`
      : `https://cloudflarestream.com/${videoId}/iframe`;
    
    const params: string[] = [];
    
    if (currentTime > 0) {
      params.push(`startTime=${currentTime}`);
    }
    
    if (!controls) {
      params.push('controls=false');
    }
    
    if (preload !== 'none') {
      params.push(`preload=${preload}`);
    }

    const queryString = params.length > 0 ? '?' + params.join('&') : '';
    return baseUrl + queryString;
  };

  const handleLoad = () => {
    onVideoLoading?.(false);
  };

  const handleError = () => {
    onVideoError?.(true);
  };

  const iframeClasses = responsive ? "absolute inset-0 w-full h-full border-0":"border-0";
  const containerClasses = responsive ? "relative w-full aspect-video":"";
  const iframeStyle: React.CSSProperties = responsive ? {} : { width, height };

  return (
    <div className={`${className} ${containerClasses}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}
      
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
          <div className="text-white text-center">
            <p className="text-sm">{t(tokens.workstation.sources.errorLoadingVideo)}</p>
            <button 
              onClick={() => {
                setHasError(false);
                setIsLoading(true);
                if (iframeRef.current) {
                  iframeRef.current.src = buildIframeUrl();
                }
              }}
              className="mt-2 px-3 py-1 bg-white/20 rounded text-xs hover:bg-white/30 transition-colors"
            >
              {t(tokens.workstation.sources.tryAgain)}
            </button>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={buildIframeUrl()}
        className={iframeClasses}
        style={iframeStyle}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        onLoad={handleLoad}
        onError={handleError}
        title={`Video ${videoId}`}
      />
    </div>
  );
};